import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { Router } from '@angular/router'
import { combineLatest, Subscription } from 'rxjs'
import { finalize, first } from 'rxjs/operators'
import { IdentityProviderDto } from 'src/app/services/dtos'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from '../../services/account.service'
import { ModalDialogComponent } from '../../../shared/modal-dialog/modal-dialog.component'
import { UiService } from 'src/app/services/ui.service'
import { ApiService } from 'src/app/services/api.service'
import { BaseError } from '../../../../utils/errors'

import { AccessLevelService, AccountPermission } from 'src/app/services/access-level.service'
import { IdpModalComponent, IdpModalData, IdpModalResult } from '../shared/idp-modal/idp-modal.component'

@Component({
  selector: 'app-account-identity-providers',
  templateUrl: './account-identity-providers.component.html',
  standalone: false,
})
export class AccountIdentityProvidersComponent implements OnInit, OnDestroy {
  @ViewChild('idpModal', { static: false }) idpModal: IdpModalComponent
  @ViewChild('deleteIdpDialog', { static: false }) deleteIdpDialog: ModalDialogComponent

  private createIdentityProviderSubscription: Subscription = null
  private updateIdentityProviderSubscription: Subscription = null
  private deleteIdentityProviderSubscription: Subscription = null
  private startIdpConsentSubscription: Subscription = null
  toDelete: IdentityProviderDto = null

  // Permission states
  private hasUpdateAccount = false
  private hasReadAccount = false
  private permissionsSubscription: Subscription = null
  isLoading = true

  get rbacEnabled(): boolean {
    return this.accountService.rbacEnabled
  }

  constructor(
    private uiService: UiService,
    private apiService: ApiService,
    public accountService: AccountService,
    private accessLevelService: AccessLevelService,
    private router: Router
  ) {}

  ngOnInit() {
    this.initializePermissions()
  }

  private initializePermissions() {
    this.permissionsSubscription = combineLatest([
      this.accessLevelService.hasAccountPermission(AccountPermission.UPDATE_ACCOUNT),
      this.accessLevelService.hasAccountPermission(AccountPermission.READ_ACCOUNT),
    ])
      .pipe(first())
      .subscribe(([hasUpdateAccount, hasReadAccount]) => {
        this.hasUpdateAccount = hasUpdateAccount
        this.hasReadAccount = hasReadAccount
        this.isLoading = false

        // Check if user has permission to view this tab
        if (!hasReadAccount && this.rbacEnabled) {
          this.uiService.announceError(
            new BaseError(
              'Insufficient permissions',
              'You need either Account Admin or Account Read Only role to access account settings.'
            )
          )
          this.router.navigate(['/', this.accountService.accountId])
        }
      })
  }

  ngOnDestroy() {
    if (subscriptionActive(this.permissionsSubscription)) {
      this.permissionsSubscription.unsubscribe()
      this.permissionsSubscription = null
    }
  }

  startMsEntraIdpAppConsentFlow(idp: IdentityProviderDto) {
    const returnUrl = this.uiService.buildAbsoluteAccountSettingsUrl(this.accountService.accountId)
    this.startIdpConsentSubscription = this.apiService
      .startMsEntraAppConsentFlow(idp.identityProviderId, returnUrl)
      .pipe(finalize(() => (this.startIdpConsentSubscription = null)))
      .subscribe(
        (consentUrl) => (window.location.href = consentUrl),
        (error: BaseError) => this.uiService.announceError(error)
      )
  }

  typeDisplay(type: string): string {
    switch (type) {
      case 'saml':
        return 'SAML'
      case 'msEntra':
        return 'Microsoft Entra'
      default:
        return type
    }
  }

  onIdpModalSave(data: IdpModalResult) {
    const modalData = this.idpModal.data
    this.idpModal.loading = true

    let serviceCall
    if (modalData.operation === 'create') {
      if (modalData.type === 'saml') {
        serviceCall = this.accountService.createSamlIdentityProvider$(
          data.name,
          data.metadata,
          data.useGlobalSpEntityId,
          data.autoCreateSubjects
        )
      } else {
        serviceCall = this.accountService.createMsEntraIdentityProvider$(
          data.name,
          data.tenantId,
          data.useForAuthentication,
          data.autoCreateSubjects
        )
      }
    } else {
      const existingIdp = modalData.existingIdp
      if (modalData.type === 'saml') {
        serviceCall = this.accountService.updateSamlIdentityProvider$(
          existingIdp.identityProviderId,
          data.name,
          data.metadata,
          data.useGlobalSpEntityId,
          data.autoCreateSubjects
        )
      } else {
        serviceCall = this.accountService.updateMsEntraIdentityProvider$(
          existingIdp.identityProviderId,
          data.name,
          data.tenantId,
          data.useForAuthentication,
          data.autoCreateSubjects
        )
      }
    }

    this.createIdentityProviderSubscription = serviceCall
      .pipe(finalize(() => (this.createIdentityProviderSubscription = null)))
      .subscribe(
        (idp: IdentityProviderDto) => {
          this.accountService.addIdentityProvider(idp)
          this.idpModal.close()
        },
        (error: BaseError) => {
          this.idpModal.loading = false
          this.uiService.announceApiError(error, 'Failed to save identity provider')
        }
      )
  }

  onIdpModalCancel() {
    // Modal handles its own closing
  }

  deleteIdp(id) {
    this.deleteIdentityProviderSubscription = this.accountService
      .deleteIdentityProvider$(id)
      .pipe(finalize(() => (this.deleteIdentityProviderSubscription = null)))
      .subscribe(
        () => {
          this.accountService.removeIdentityProvider(id)
          this.dismissDeleteConfirmModal()
        },
        (error) => {
          this.deleteIdpDialog.error =
            'Unable to delete this identity provider. Please try again later. If the issue persists, please contact support.'
        }
      )
  }

  dismissDeleteConfirmModal() {
    this.toDelete = null
    this.deleteIdpDialog.close()
  }

  openDeleteConfirmModal(idp: IdentityProviderDto) {
    this.toDelete = idp
    this.deleteIdpDialog.open()
  }

  openAddSamlIdpDialog() {
    const modalData: IdpModalData = {
      type: 'saml',
      operation: 'create',
    }
    this.idpModal.open(modalData)
  }

  openAddMsEntraIdpDialog() {
    const modalData: IdpModalData = {
      type: 'msEntra',
      operation: 'create',
    }
    this.idpModal.open(modalData)
  }

  openEditIdpDialog(idp: IdentityProviderDto) {
    if (idp.config && (idp.config.type === 'saml' || idp.config.type === 'msEntra')) {
      const modalData: IdpModalData = {
        type: idp.config.type as 'saml' | 'msEntra',
        operation: 'edit',
        existingIdp: idp,
      }
      this.idpModal.open(modalData)
    } else {
      console.error(`Unsupported identity provider type: ${idp.config?.type}`)
    }
  }

  get canManageIdentityProviders() {
    return this.hasUpdateAccount
  }

  get canViewIdentityProviders() {
    // During loading, return true to show the tab content with spinner
    // After loading, return based on actual permission
    return this.isLoading || this.hasReadAccount
  }

  get isContentReady() {
    return !this.isLoading && this.canDisplayEntitlementContent
  }

  get canDisplayEntitlementContent(): boolean {
    return this.accountService.entitlementLoaded
  }
}
